import { shouldIncludeBenefitInApi } from "./typeAmountTransforms";

import type {
  AgeReductionScheduleItem,
  ApiAgeReductionScheduleItem,
} from "./types";

/**
 * Maps a schedule's frontend field names (age, reductionPercent) to the API's
 * field names (ageBandLower, ageBandUpper, percent). Calculates ageBandUpper
 * from the next entry's ageBandLower - 1; the last entry gets ageBandUpper =
 * 120. Returns `undefined` when there is nothing to send.
 */
export function mapAgeReductionBandsToApi(
  schedulesValue?: AgeReductionScheduleItem[],
): ApiAgeReductionScheduleItem[] | undefined {
  if (!Array.isArray(schedulesValue) || schedulesValue.length === 0) {
    return undefined;
  }

  const sortedSchedules = [...schedulesValue].sort((a, b) => {
    const ageA = typeof a.age === "number" ? a.age : 0;
    const ageB = typeof b.age === "number" ? b.age : 0;
    return ageA - ageB;
  });

  const schedules = sortedSchedules.map(
    (item: AgeReductionScheduleItem, index: number) => {
      const schedule: ApiAgeReductionScheduleItem = {};

      if (item.age !== undefined) {
        schedule.ageBandLower = item.age;
      }

      if (item.age !== undefined) {
        const isLastEntry = index === sortedSchedules.length - 1;
        if (isLastEntry) {
          schedule.ageBandUpper = 120;
        } else {
          const nextAge = sortedSchedules[index + 1]?.age;
          if (nextAge !== undefined && typeof nextAge === "number") {
            schedule.ageBandUpper = nextAge - 1;
          }
        }
      }

      if (item.reductionPercent !== undefined) {
        schedule.percent = item.reductionPercent;
      }

      return schedule;
    },
  );

  return schedules.length > 0 ? schedules : undefined;
}

/**
 * Maps a schedule's API field names (ageBandLower, percent) back to the
 * frontend's field names (age, reductionPercent), sorted by ageBandLower
 * because carriers don't always send bands in age order. ageBandUpper is
 * dropped — it's recalculated on the frontend based on the next entry.
 */
export function mapAgeReductionBandsFromApi(
  schedules: ApiAgeReductionScheduleItem[] | null | undefined,
): AgeReductionScheduleItem[] {
  if (!Array.isArray(schedules)) return [];

  const sortedSchedules = [...schedules].sort(
    (a, b) => (a.ageBandLower ?? 0) - (b.ageBandLower ?? 0),
  );

  return sortedSchedules.map((item) => ({
    ...(item.ageBandLower !== undefined ? { age: item.ageBandLower } : {}),
    ...(item.percent !== undefined ? { reductionPercent: item.percent } : {}),
  }));
}

/**
 * Transforms age reduction schedule from grid format to API format.
 *
 * Maps frontend field names (age, reductionPercent) to API field names
 * (ageBandLower, ageBandUpper, percent). Calculates ageBandUpper from
 * the next entry's ageBandLower - 1; last entry gets ageBandUpper = 120.
 *
 * @param typeValue - The age reduction schedule type
 * @param schedulesValue - The schedules array with frontend field names
 */
export function transformAgeReductionScheduleToApi(
  typeValue: string | null | undefined,
  schedulesValue?: AgeReductionScheduleItem[],
): { type: string; schedules?: ApiAgeReductionScheduleItem[] } | undefined {
  if (!shouldIncludeBenefitInApi(typeValue)) {
    return undefined;
  }

  const result: { type: string; schedules?: ApiAgeReductionScheduleItem[] } = {
    type: typeValue,
  };

  const shouldExcludeSchedules =
    typeValue === "notIncluded" ||
    typeValue === "notOffered" ||
    typeValue === "notApplicable";

  if (!shouldExcludeSchedules) {
    const schedules = mapAgeReductionBandsToApi(schedulesValue);
    if (schedules) {
      result.schedules = schedules;
    }
  }

  return result;
}

/**
 * Transforms age reduction schedule from API format to grid format.
 *
 * Maps API field names (ageBandLower, percent) back to frontend field names
 * (age, reductionPercent), sorted by age. ageBandUpper is ignored — it's
 * recalculated on the frontend based on the next entry.
 *
 * @param apiObject - The API object with type and schedules array
 */
export function transformAgeReductionScheduleFromApi(apiObject: unknown): {
  ageReductionScheduleType: string | undefined;
  ageReductionScheduleSchedules: AgeReductionScheduleItem[];
} {
  const result: {
    ageReductionScheduleType: string | undefined;
    ageReductionScheduleSchedules: AgeReductionScheduleItem[];
  } = {
    ageReductionScheduleType: undefined,
    ageReductionScheduleSchedules: [],
  };

  if (apiObject && typeof apiObject === "object") {
    const obj = apiObject as Record<string, unknown>;
    result.ageReductionScheduleType = obj["type"] as string | undefined;

    if (Array.isArray(obj["schedules"])) {
      result.ageReductionScheduleSchedules = mapAgeReductionBandsFromApi(
        obj["schedules"] as ApiAgeReductionScheduleItem[],
      );
    }
  }

  return result;
}
